import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { Utils } from '@pbt/pbt-ui-components'

import {
  getDiagnosisProblemStates,
  getDiagnosisStatus,
  getFindingProblemStates,
} from '~/store/reducers/constants'
import {
  type DiagnosisSnapshotItem,
  DiagnosisSnapshotItemType,
  type MasterProblemsSnapshotItem,
  type ProblemDiagnosisLog,
  ProblemStates,
} from '~/types'

interface UseDiagnosesPartitioningReturn {
  AddedByAiDiagnosisStatusId: string
  RemovedDiagnosisStatusId: string
  RemovedFindingStatusId: string
  ResolvedDiagnosisStatusId: string
  ResolvedFindingStatusId: string
  RuledOutDiagnosisStatusId: string
  activeProblems: MasterProblemsSnapshotItem[]
  byLatestDate: (
    a: MasterProblemsSnapshotItem,
    b: MasterProblemsSnapshotItem,
  ) => number
  groupedDiagnoses: MasterProblemsSnapshotItem[]
  historicProblems: MasterProblemsSnapshotItem[]
  isNotRemoved: (diagnosis: MasterProblemsSnapshotItem) => boolean
  isResolved: (diagnosis: MasterProblemsSnapshotItem) => boolean
}

export const useDiagnosesPartitioning = (
  diagnoses: DiagnosisSnapshotItem[],
): UseDiagnosesPartitioningReturn => {
  const DiagnosisStatus = useSelector(getDiagnosisProblemStates)
  const NonProblemDiagnosisStatus = useSelector(getDiagnosisStatus)
  const FindingStatus = useSelector(getFindingProblemStates)

  const ResolvedDiagnosisStatusId = Utils.findConstantIdByName(
    'Resolved',
    DiagnosisStatus,
  )
  const RemovedDiagnosisStatusId = Utils.findConstantIdByName(
    'Removed',
    DiagnosisStatus,
  )
  const ResolvedFindingStatusId = Utils.findConstantIdByName(
    'Resolved',
    FindingStatus,
  )
  const RemovedFindingStatusId = Utils.findConstantIdByName(
    'Removed',
    FindingStatus,
  )
  const RuledOutDiagnosisStatusId = Utils.findConstantIdByName(
    'Ruled Out',
    DiagnosisStatus,
  )
  const AddedByAiDiagnosisStatusId = Utils.findConstantIdByName(
    ProblemStates.ADDED_BY_AI,
    NonProblemDiagnosisStatus,
  )

  const groupedDiagnoses: MasterProblemsSnapshotItem[] = R.curry((problems) =>
    R.pipe(
      R.filter(
        (cur: DiagnosisSnapshotItem) =>
          !(
            cur.type === DiagnosisSnapshotItemType.DIAGNOSIS_HISTORY &&
            cur.statusId === AddedByAiDiagnosisStatusId
          ),
      ),
      R.groupBy((diagnosis: ProblemDiagnosisLog) => diagnosis.problemId),
      R.values,
      R.map(R.sort(R.descend(R.prop('date')))),
      R.map((problemGroup) => ({
        ...R.head(problemGroup),
        children: problemGroup,
      })),
    )(problems),
  )(diagnoses)

  const isResolved = (diagnosis: MasterProblemsSnapshotItem) =>
    (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
      diagnosis.statusId === ResolvedDiagnosisStatusId) ||
    (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG &&
      diagnosis.statusId === ResolvedFindingStatusId)

  const isNotRemoved = (diagnosis: MasterProblemsSnapshotItem) =>
    !(
      (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
        (diagnosis.statusId === RemovedDiagnosisStatusId ||
          diagnosis.statusId === RuledOutDiagnosisStatusId)) ||
      (diagnosis.type === DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG &&
        diagnosis.statusId === RemovedFindingStatusId)
    )

  const byLatestDate = R.descend((diagnosis: MasterProblemsSnapshotItem) =>
    new Date(diagnosis.date).getTime(),
  )

  const [historicProblems, activeProblems] = R.partition(
    isResolved,
    groupedDiagnoses.filter(isNotRemoved),
  )

  return {
    ResolvedDiagnosisStatusId,
    RemovedDiagnosisStatusId,
    ResolvedFindingStatusId,
    RemovedFindingStatusId,
    RuledOutDiagnosisStatusId,
    AddedByAiDiagnosisStatusId,
    groupedDiagnoses,
    isResolved,
    isNotRemoved,
    byLatestDate,
    historicProblems,
    activeProblems,
  }
}
